import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { decodeJwt, JWTPayload } from 'jose';
import { Observable, map, catchError, of } from 'rxjs';
import { safeErrorSummary } from '../../helper/safe-error-summary';

export interface UserInfo {
  name: string | null;
  email: string | null;
}

/** Auth0-issued JWT claims this service reads, beyond jose's registered claim set. */
interface Auth0JwtPayloadI extends JWTPayload {
  name?: string;
  given_name?: string;
  nickname?: string;
  email?: string;
  custom_email_claim?: string;
}

@Injectable({
  providedIn: 'root',
})
export class JwtAuthService {
  private authService = inject(AuthService);

  /**
   * Retrieves user information from the JWT token
   * @returns Observable<UserInfo | null> containing user name and email or null if token is invalid
   */
  getUserInfoFromToken(): Observable<UserInfo | null> {
    return this.authService.getAccessTokenSilently().pipe(
      map((token: string) => {
        try {
          const decodedJwt = decodeJwt(token) as Auth0JwtPayloadI;

          // Extract user information from JWT payload
          // Check for common JWT claims
          const name = decodedJwt.name || decodedJwt.given_name || decodedJwt.nickname || null;

          const email = decodedJwt.email || decodedJwt.custom_email_claim || null;

          return {
            name,
            email,
          };
        } catch (error) {
          // #2133: a decoded JWT carries claims (name/email) — never log the raw
          // decode error, only a safe name/message summary.
          console.error('Error decoding JWT token:', safeErrorSummary(error));
          return null;
        }
      }),
      catchError(error => {
        console.error('Error getting access token:', safeErrorSummary(error));
        return of(null);
      }),
    );
  }

  /**
   * Gets the display name for the user (name or email as fallback)
   * @returns Observable<string> containing the display name or 'Admin' as fallback
   */
  getUserDisplayName(): Observable<string> {
    return this.getUserInfoFromToken().pipe(
      map((userInfo: UserInfo | null) => {
        if (userInfo?.name) {
          return userInfo.name;
        }
        if (userInfo?.email) {
          return userInfo.email;
        }
        return 'Admin';
      }),
    );
  }
}
